
import { useApiStore } from "@/stores/apiStore";
import Cropper from "cropperjs";
import { useAuthStore } from "@/stores/authStore";
import { useToastStore } from "@/stores/toastStore";
import { defineComponent, Ref } from "vue";
import { storeToRefs } from "pinia";
import moment from "moment-timezone";
import { AxiosError } from "axios";
import i18n from "@/utils/i18n";
export default defineComponent({
	setup() {
		const api = useApiStore();
		const authStore = useAuthStore();
		const toast = useToastStore();
		const { user }: { user: Ref<AuthUser | null> } = storeToRefs(authStore);
		const authUser = user;
		return { api, authStore, authUser, moment, toast, i18n };
	},
	name: "imdex-user",
	data() {
		return {
			userDeleteDialog: false,
			createLoading: false,
			approvePending: false,
			denyPending: false,
			tableLoading: false,
			avatarLoading: false,
			email: "",
			showOption: false, // eslint-disable-next-line
			cropper: null as any,
			cropperDialog: false,
			previewUrl: "",
			url: "",
			siteData: {
				columns: ["site", "contributor", "viewer", "none"],
				tableData: [],
				options: {}
			},
			user: {
				name: "",
				email: ""
			} as AuthUser,
			roleSelect: "New User",
			userRole: "New User",
			availableRoles: [],
			allRoles: [],
			sites: [],
			profilePicMap: {} as any
		};
	},
	async mounted() {
		await this.getUser();
		if (this.user.profilePicUrl) {
			await this.getProfileImage(this.user.profilePicUrl);
		}
		this.getSites();
		if (this.isSysAdmin) {
			this.getRoles();
		}
	},
	computed: {
		isMe(): boolean {
			// eslint-disable-next-line
			return this.$route.params.id == this.authUser!.id;
		},
		isAdmin(): boolean {
			if (this.authUser != null) {
				return (
					this.authUser.globalRole.name == "Administrator" ||
					this.authUser.globalRole.name == "System Administrator"
				);
			} else {
				return false;
			}
		},
		isSysAdmin(): boolean {
			if (this.authUser != null) {
				return this.authUser.globalRole.name == "System Administrator";
			} else {
				return false;
			}
		},
		hasAnySiteEnabled() {
			// Check if any row has either "contributor" or "viewer" enabled
			// throws compile errors on load without the (this as any)!!
			return (this as any).siteData.tableData.some(
				(row: any) => row.contributor || row.viewer
			);
		},
		renderKey() {
			return `?t=${Math.floor(Math.random() * 6) + 1}`;
		},
		editing() {
			return this.$route.params.id != null;
		}
	},
	methods: {
		async getProfileImage(imgUrl: string) {
			const response = await this.api.get(imgUrl, { responseType: "blob" });
			const blob = new Blob([response.data], { type: "image/png" });
			const objectUrl = URL.createObjectURL(blob);

			this.profilePicMap[imgUrl] = objectUrl;
			const profilePicElement = document.getElementById("profile-pic");
			(profilePicElement as HTMLImageElement).onload = () => URL.revokeObjectURL(objectUrl);
		},
		validateUserInputs() {
			let errorMessage = "";
			const isNameTooShort = this.user.name && this.user.name.length < 3;

			if (!this.user.name || (this.user.name && !this.user.name.trim()) || isNameTooShort) {
				if (!this.user.email) {
					errorMessage = this.$t(`userProfile.errorInputsBlank`);
				} else if (isNameTooShort) {
					errorMessage = this.$t(`userProfile.errorInputsLength`);
				} else {
					errorMessage = this.$t(`userProfile.errorInputsName`);
				}

				this.createLoading = false;

				this.toast.toast({
					title: this.$t("toastGeneralError"), // toastGeneralError ?
					dismissable: false,
					message: errorMessage,
					type: "error"
				});

				return false;
			}
			return true;
		},
		changeAvatar() {
			(this.$refs["avatar-image"] as HTMLElement).click();
		},
		async finishCrop() {
			this.avatarLoading = true; // eslint-disable-next-line
			this.cropper.getCroppedCanvas().toBlob(async (blob: any) => {
				const formData = new FormData();
				formData.append("avatar", blob, "avatar.png");
				const urlCreator = window.URL || window.webkitURL;
				this.previewUrl = urlCreator.createObjectURL(blob); // eslint-disable-next-line
				if (this.isMe) {
					await this.api.post({ endpoint: `/user/avatar-upload`, post: formData });
				} else {
					await this.api.post({
						endpoint: `/user/avatar-upload/${this.$route.params.id}`,
						post: formData
					});
				}
			});
			this.cropperDialog = false;
		}, // eslint-disable-next-line
		onFileChange(e: any) {
			this.url = "";
			const file = e.target.files[0];
			const validImageTypes = ["image/jpeg", "image/png"];
			if (!validImageTypes.includes(file.type)) {
				this.toast.toast({
					title: this.$t("toastGeneralError"),
					dismissable: false,
					message: this.$t(`userProfile.errorAvaFileType`),
					type: "error"
				});

				return;
			}
			// Check file size
			const maxSizeInBytes = 3 * 1024 * 1024; // 3MB
			if (file.size > maxSizeInBytes) {
				this.toast.toast({
					title: this.$t("toastGeneralError"),
					dismissable: false,
					message: this.$t(`userProfile.errorAvaFileSize`),
					type: "error"
				});

				return;
			}
			this.cropperDialog = true;

			this.$nextTick(() => {
				this.url = URL.createObjectURL(file);
				const image: HTMLImageElement = this.$refs["cropper-image"] as HTMLImageElement;

				this.$nextTick(() => {
					if (this.cropper) {
						this.cropper.replace(this.url);
					} else {
						this.cropper = new Cropper(image, {
							aspectRatio: 16 / 16,
							minContainerHeight: 300,
							viewMode: 1,
							background: false,
							guides: false
						});
					}
				});
			});
		},
		async deleteUser() {
			await this.api.delete(`/user/delete/${this.$route.params.id}`);
			this.goBack();
		},
		async getRoles() {
			const { data } = await this.api.get("/role/read"); // eslint-disable-next-line
			this.allRoles = data.map((item: any) => {
				return {
					id: item.id,
					name: item.name,
					text: item.name,
					value: item.name,
					global: item.global
				};
			}); // eslint-disable-next-line
			this.availableRoles = this.allRoles.filter((item: any) => item.global == true);
		},
		async getSites() {
			try {
				const route =
					this.isMe || !this.editing
						? `/user/read-site-roles`
						: `/user/read-site-roles/${this.$route.params.id}`;
				const { data } = await this.api.get(route);
				// eslint-disable-next-line

				this.siteData.tableData = data.reduce((acc: any, curr: any) => {
					if (!this.isSysAdmin && curr.isSysAdminOnly) {
						return acc;
					} else {
						let role = curr.availableRoles.filter((item: any) => item.currentRole);
						let roleString = "";
						if (role.length > 0) {
							roleString = role[0].name.toLowerCase();
						} else {
							role = null;
						} // eslint-disable-next-line
						let roleObj: any = {
							site: curr.name,
							siteId: curr.id,
							isSysAdminOnly: curr.isSysAdminOnly || false,
							contributor: false,
							viewer: false,
							none: false
						};
						if (role != null) {
							roleObj[roleString] = true;
						} else {
							roleObj.none = true;
						}

						acc.push(roleObj);

						return acc;
					}
				}, []);
			} catch (e) {
				console.log(e);
			}
		},
		async getUser() {
			this.avatarLoading = true;
			if (!this.editing) return;
			let route = "/user/read/" + this.$route.params.id; // eslint-disable-next-line
			if (this.isMe) {
				route = "/user/read/me";
			}
			try {
				const { data } = await this.api.get(route);
				this.user = data as AuthUser;
				this.roleSelect = data.globalRole.name;
				this.userRole = data.globalRole.name;
				// eslint-disable-next-line
				if (this.$route.params.id == this.authUser!.id) {
					this.authStore.setUser(this.user);
				}
				this.avatarLoading = false;
			} catch (e) {
				this.authStore.logout();
			}
		},
		async denyUser() {
			this.denyPending = true;
			this.doSubmit(false);
		},
		async approveUser() {
			this.approvePending = true;
			this.doSubmit(true);
		},
		async activateUser() {
			this.user.isActive = true;
			this.doSubmit(null);
			this.showOption = false;
		},
		async deactivateUser() {
			this.user.isActive = false;
			this.doSubmit(null);
			this.showOption = false;
		},
		async submitEditUser() {
			this.doSubmit(null);
		},
		async doSubmit(approveDeny: boolean | null) {
			this.createLoading = true;

			try {
				if (!this.validateUserInputs()) return;
				if (this.isMe) {
					await this.api.put({ endpoint: "/user/update/", post: this.user });
				} else {
					await this.api.put({
						endpoint: "/user/update/" + this.$route.params.id,
						post: this.user
					});
				} // eslint-disable-next-line
				if (
					this.authUser!.globalRole.name == "Administrator" ||
					this.authUser!.globalRole.name == "System Administrator"
				) {
					if (this.roleSelect != this.userRole) {
						// eslint-disable-next-line
						let roleId: any = this.availableRoles.filter(
							(item: any) => item.name == this.roleSelect
						)[0];
						const rolePost = {
							userId: this.$route.params.id,
							roleId: roleId.id
						};
						await this.api.post({ endpoint: "/user/assign-role", post: rolePost });
					}
				}

				// Default case: Save button clicked
				let toastMessage = this.$t(`userProfile.toastSuccessUserUpdate`);

				// User clicked Approve or Deny instead
				if (approveDeny != null) {
					// Reload user prior to approve/deny
					this.getUser();
					const approvePost = {
						id: this.user.id,
						isApproved: approveDeny
					};
					await this.api.put({
						endpoint: "/user/approve-access/" + this.$route.params.id,
						post: approvePost
					});
					toastMessage = approveDeny
						? this.$t(`userProfile.toastSuccessUserApproved`)
						: this.$t(`userProfile.toastSuccessUserDenied`);
				}

				this.getUser();
				this.toast.toast({
					title: this.$t("toastGeneralSuccess"),
					dismissable: false,
					message: toastMessage,
					type: "success"
				});
			} catch (e) {
				this.toast.toast({
					title: this.$t("toastGeneralError"),
					dismissable: false,
					message: this.$t(`userProfile.errorGeneralEdit`),
					type: "error"
				});
			} finally {
				this.createLoading = false;
				this.approvePending = false;
				this.denyPending = false;
			}
		},
		async submitCreateUser() {
			this.createLoading = true;

			if (!this.validateUserInputs()) return;

			try {
				await this.api
					.post({ endpoint: "/user/create", post: this.user })
					.then(async ({ data }: { data: any }) => {
						if (this.roleSelect != this.userRole) {
							// eslint-disable-next-line
							const role: any = this.availableRoles.find(
								(role: any) => role.name === this.roleSelect
							);
							const rolePost = {
								userId: data.id,
								roleId: role.id
							};

							await this.api.post({ endpoint: "/user/assign-role", post: rolePost });
						}
					});
				this.createLoading = false;
				this.goBack();
			} catch (e) {
				if (e instanceof AxiosError) {
					this.createLoading = false;
					this.toast.toast({
						title: this.$t("toastGeneralError"),
						dismissable: false,
						message: e.response
							? e.response.data.message
							: this.$t(`userProfile.errorCreatingUser`),
						type: "error"
					});
				}
			}
		}, // eslint-disable-next-line
		async updateSitePermission(row: any, type: string) {
			try {
				const post = {
					userId: this.$route.params.id,
					roleId: undefined,
					roleName: type === "none" ? "none" : undefined,
					siteId: row.siteId
				};

				if (type !== "none") {
					const role: any = this.allRoles.filter((item: any) => {
						return item.name.toLowerCase() == type;
					});

					const roleId = role.length ? role[0].id : null;

					if (role) {
						post.roleId = roleId;
					}
				}

				await this.api.post({ endpoint: "/user/assign-role", post });
			} catch (e) {
				console.log(e);
			}
			row.contributor = false;
			row.viewer = false;
			row.none = false;
			row[type] = true;
		},
		goBack() {
			if (window.history.length > 1) {
				this.$router.go(-1);
			} else {
				if (this.isAdmin) {
					this.$router.push("/users");
				} else {
					this.$router.push("/ssa");
				}
			}
		}
	},
	watch: {
		$route() {
			if (this.$route.name === "Edit User") {
				this.getUser();
			}
		}
	}
});
